import { useCallback, useMemo, useState, useRef } from 'react';
import MojitoPresentation from 'mojito/presentation';
import MojitoServices from 'mojito/services';
import GenericMarketContent from 'modules/aggregated-market-pane/generic/index.js';
import { useMarketItems } from 'modules/common/hooks';

const { FlexPane, Text, Swiper, SwiperUtils, MenuRow } = MojitoPresentation.Components;
const EventsUtils = MojitoServices.SportsContent.Events.utils;
const { types: MarketGroupTypes } = MojitoServices.SportsContent.MarketGroups;
const { createAggregatedMarket } = EventsUtils;
const { AGGREGATED_MARKET_TYPE } = MarketGroupTypes;

const EventCardMarketsSlider = props => {
    const { event, mojitoTools, shouldRequestData, marketId, isCardHovered } = props;
    const { config } = mojitoTools;
    const { maxMarkets } = config;
    const swiperContainerRef = useRef();
    const [activeSlideIndex, setActiveSlideIndex] = useState(0);

    const marketIds = useMemo(() => {
        const lineIds = Object.values(event.marketLines || {})
            .map(line => line?.id)
            .filter(Boolean);
        // Keep the promoted market first without duplicating it.
        const ids = marketId ? [marketId, ...lineIds.filter(id => id !== marketId)] : lineIds;

        return maxMarkets > 0 ? ids.slice(0, maxMarkets) : ids;
    }, [event.marketLines, marketId, maxMarkets]);

    const markets = useMarketItems(marketIds, shouldRequestData);
    const marketsById = useMemo(
        () => new Map(markets.map(market => [market.id, market])),
        [markets]
    );

    const onSwiperRef = useCallback(node => {
        swiperContainerRef.current = node;
    }, []);

    const onDotClicked = useCallback(dotId => {
        const index = Number(dotId);
        if (swiperContainerRef.current) {
            SwiperUtils.scrollToSlide(swiperContainerRef.current, index);
        }
        setActiveSlideIndex(index);
    }, []);

    if (!marketIds.length) {
        return null;
    }

    const dotItems = marketIds.map((id, index) => ({
        id: String(index),
        data: '',
        className: 'ta-marketDot',
    }));

    return (
        <FlexPane class="ta-EventCardMarketsSlider" config={config.container}>
            <Swiper
                config={config.swiper}
                elementRef={onSwiperRef}
                onSlideChange={setActiveSlideIndex}
                forceNavigationArrows={isCardHovered}
            >
                {marketIds.map(id => (
                    <MarketSlide
                        key={id}
                        marketId={id}
                        market={marketsById.get(id)}
                        event={event}
                        config={config}
                    />
                ))}
            </Swiper>
            {dotItems.length > 1 && (
                <FlexPane config={config.paginationContainer}>
                    <MenuRow
                        class="ta-marketsSliderDots"
                        config={config.paginationDots}
                        menuItems={dotItems}
                        selectedItemId={String(activeSlideIndex)}
                        cbItemClicked={onDotClicked}
                    />
                </FlexPane>
            )}
        </FlexPane>
    );
};

const MarketSlide = props => {
    const { marketId, market, event, config } = props;
    const marketInfo = { type: AGGREGATED_MARKET_TYPE.GENERIC, markets: [{ id: marketId }] };
    const isPending = !market;

    return (
        <FlexPane config={config.slideContainer}>
            {config.showMarketTitle && (
                <Text class="ta-marketTitle" config={config.marketTitle}>
                    {market?.name}
                </Text>
            )}
            <GenericMarketContent
                config={config.genericMarketContent}
                aggregatedMarket={createAggregatedMarket(marketInfo, market ? [market] : [])}
                eventItem={event}
                showSelectionHeaders={false}
                isPending={isPending}
            />
        </FlexPane>
    );
};

export default EventCardMarketsSlider;
